<i18n
  src="@/locales/views/customer/reporting/container/show_template_main_rep.yml"
  lang="yaml"
></i18n>
<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    name="customer_reporting_container_list_reports_path"
    :params="{
      customer_id: customer_id,
      agro_exporter_id: agro_exporter_id,
      agro_exporter_process_id: agro_exporter_process_id,
      container_id: container_id
    }"
  />
  <bread-crumbs
    :labels="[
      module_name,
      agro_exporter?.name,
      agro_exporter_process?.name,
      container?.container_code,
      quality_template_main_rep?.name
    ]"
  ></bread-crumbs>

  <template v-if="load_data_general">
    <container-info :container="container"></container-info>

    <h1 class="page-title mt-8">
      <i class="fa-solid fa-box-check"></i><span class="space-icon"></span>
      {{ $t("labels.title") }}
    </h1>

    <div class="mt-8">
      <div class="flex items-center">
        <div class="grow grid grid-cols-2 gap-x-1">
          <date-form v-model="search_params.registered_at" />
          <select-form
            v-if="producers.length > 1"
            class="bg-white rounded-md"
            v-bind:placeholder="'Productor'"
            v-model="search_params.producer"
            :options="producers"
            :multiple="true"
          />
          <select-form
            v-if="organizations.length > 1"
            class="bg-white rounded-md"
            v-bind:placeholder="'Organización'"
            v-model="search_params.organization"
            :options="organizations"
            :multiple="true"
          />
        </div>
        <div class="">
          <span class="space-actions"></span>
          <button class="btn-white" @click="fetchRepGuiData">
            <i class="fa-solid fa-filter"></i><span class="space-icon"></span>
            {{ $t("labels.filter_button") }}
          </button>
          <span class="space-actions"></span>
          <button class="btn-white" @click="downloadReportXlsx">
            <i
              :class="[
                'fa-solid',
                downloading ? 'fa-sync fa-spin' : 'fa-download'
              ]"
            ></i
            ><span class="space-icon"></span>
            {{ $t("labels.download_button") }}
          </button>
        </div>
      </div>

      <qr-table
        :rep_table_headers="rep_table_headers"
        :rep_table_data="rep_table_data"
        :fetching="fetching"
      ></qr-table>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <div v-for="(rep_graphic, i) in rep_graphics" :key="i">
          <qr-graphic :rep_graphic="rep_graphic"></qr-graphic>
        </div>
      </div>
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/customer/requesters/reporting/container/show_template_main_rep.js";
import QrTable from "@/components/views/layout/quality_reports/QrTable.vue";
import QrGraphic from "@/components/views/layout/quality_reports/QrGraphic.vue";
import ContainerInfo from "@/components/views/customer/reporting/ContainerInfo.vue";

export default {
  name: "CustomerReportingContainerShowTemplateMainRep",
  components: { QrTable, QrGraphic, ContainerInfo },
  data() {
    return {
      load_data_general: false,
      downloading: false,
      fetching: false,
      agro_exporter: {},
      agro_exporter_process: {},
      container: {},
      quality_template_main_rep: {},
      rep_table_headers: [],
      rep_table_data: [],
      rep_graphics: [],
      producers: [
        {
          id: 0,
          name: this.$t("labels.all_producers")
        }
      ],
      organizations: [
        {
          id: 0,
          name: this.$t("labels.all_organizations")
        }
      ],
      search_params: {
        producer: [0],
        organization: [0],
        registered_at: new Date()
      }
    };
  },
  props: [
    "customer_id",
    "agro_exporter_id",
    "agro_exporter_process_id",
    "container_id",
    "quality_template_main_rep_id"
  ],
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          customer_id: this.customer_id
        }
      }).then(() => {
        this.fetchData();
      })
    );
  },

  methods: {
    fetchData: async function () {
      await this.fetchAgroExporter();
      await this.fetchAgroExporterProcess();
      await this.fetchContainer();
      await this.fetchQualityTemplateMainRep();
      await this.fetchProducers();
      await this.fetchOrganizations();
      await this.fetchRepGuiHeaders();
      this.load_data_general = true;
    },
    fetchAgroExporter: async function () {
      let response = await Requester.fetchAgroExporter(
        this.customer_id,
        this.agro_exporter_id
      );
      this.agro_exporter = response.data;
    },
    fetchAgroExporterProcess: async function () {
      let response = await Requester.fetchAgroExporterProcess(
        this.customer_id,
        this.agro_exporter_process_id
      );
      this.agro_exporter_process = response.data;
    },
    fetchContainer: async function () {
      let response = await Requester.fetchContainer(
        this.customer_id,
        this.container_id
      );
      this.container = response.data;
    },
    fetchQualityTemplateMainRep: async function () {
      let response = await Requester.fetchQualityTemplateMainRep(
        this.customer_id,
        this.quality_template_main_rep_id
      );
      this.quality_template_main_rep = response.data;
    },
    fetchProducers: async function () {
      let response = await Requester.fetchProducers(
        this.customer_id,
        this.container_id
      );
      this.producers.push(...response.data);
    },
    fetchOrganizations: async function () {
      let response = await Requester.fetchOrganizations(
        this.customer_id,
        this.container_id
      );
      this.organizations.push(...response.data);
    },
    fetchRepGuiHeaders: async function () {
      let response = await Requester.fetchRepGuiHeaders(
        this.customer_id,
        this.container_id,
        this.quality_template_main_rep_id
      );
      this.rep_table_headers = response.data;
    },
    fetchRepGuiData: async function () {
      this.rep_table_data = [];
      this.rep_graphics = [];
      this.fetching = true;
      let response = await Requester.fetchRepGuiData(
        this.customer_id,
        this.container_id,
        this.quality_template_main_rep_id,
        {
          producer: this.search_params.producer,
          organization: this.search_params.organization,
          registered_at: this.search_params.registered_at || ""
        }
      );
      this.rep_table_data = response.data.rows;
      this.rep_graphics = response.data.graphics;
      this.fetching = false;
    },
    downloadReportXlsx: async function () {
      this.downloading = true;
      var response = await Requester.downloadRepXlsx(
        this.customer_id,
        this.container_id,
        this.quality_template_main_rep_id,
        {
          producer: this.search_params.producer,
          organization: this.search_params.organization,
          registered_at: this.search_params.registered_at || ""
        }
      );
      const file = response.data;
      var fileURL = window.URL.createObjectURL(new Blob([file]));
      var fileLink = document.createElement("a");

      fileLink.href = fileURL;
      fileLink.setAttribute(
        "download",
        `${this.quality_template_main_rep.name}-${this.container.container_code}.xlsx`
      );
      document.body.appendChild(fileLink);

      fileLink.click();
      this.downloading = false;
    }
  },
  watch: {
    search_params: {
      handler: function () {
        let producer_ids = this.search_params.producer;
        let organization_ids = this.search_params.organization;
        if (producer_ids.length == 0) {
          this.search_params.producer = [0];
        }
        if (organization_ids.length == 0) {
          this.search_params.organization = [0];
        }
        if (producer_ids.length > 1) {
          if (producer_ids.includes(0)) {
            this.search_params.producer = producer_ids.filter(id => id != 0);
          }
        }
        if (organization_ids.length > 1) {
          if (organization_ids.includes(0)) {
            this.search_params.organization = organization_ids.filter(
              id => id != 0
            );
          }
        }
      },
      deep: true
    }
  }
};
</script>
